'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'
import { cn } from '@/lib/utils'
import { Icon } from './icon'
import type { CartaoPayload } from '@/lib/export/types'

interface MobileResultBarProps {
  payload: CartaoPayload
  /** Ref do cartão de resultado — a barra some quando ele entra em vista. */
  cardRef: RefObject<HTMLElement | null>
}

/**
 * Barra de resultado fixa no rodapé (só mobile, protótipo mobile.jsx).
 * Mostra o número-herói ao vivo enquanto a pessoa preenche os inputs; ao tocar
 * "Ver conta", rola até o cartão (com detalhamento, avisos e exportação).
 * Auto-oculta via IntersectionObserver quando o cartão está visível — evita
 * mostrar o valor duas vezes.
 */
export function MobileResultBar({ payload, cardRef }: MobileResultBarProps) {
  const [cardVisible, setCardVisible] = useState(false)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => setCardVisible(entry.isIntersecting), {
      threshold: 0.25,
    })
    io.observe(el)
    return () => io.disconnect()
  }, [cardRef])

  const isNeg = payload.tone === 'neg'

  return (
    <div
      ref={barRef}
      aria-hidden={cardVisible}
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 shadow-[0_-6px_16px_rgba(0,0,0,0.16)] transition-transform duration-300 lg:hidden',
        cardVisible ? 'translate-y-full' : 'translate-y-0',
        isNeg ? 'bg-neg' : 'bg-brand'
      )}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className={cn('text-[11px]', isNeg ? 'text-white/80' : 'text-on-brand-soft')}>
            {payload.heroLabel}
          </div>
          <div className="flex items-baseline gap-1">
            <span className="truncate font-mono text-[26px] font-semibold leading-none text-white tabular-nums">
              {payload.heroValue}
            </span>
            {payload.heroSuffix && (
              <span className={cn('text-[13px]', isNeg ? 'text-white/70' : 'text-on-brand-softer')}>
                {payload.heroSuffix}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="flex shrink-0 items-center gap-1.5 bg-white/[0.16] px-3.5 py-2.5 text-[12.5px] font-medium text-white"
        >
          Ver conta
          <Icon name="chevron" size={15} className="-rotate-90" />
        </button>
      </div>
    </div>
  )
}
